import React, { useState, useEffect } from 'react';
import { signatureService } from '../services/signatureService';
import { Calendar, User, FileText, MapPin, Check, AlertCircle } from 'lucide-react';

/**
 * SignatureDetails component for displaying detailed information about a specific signature
 * 
 * @param {Object} props
 * @param {string} props.signatureId - ID of the signature to display
 * @param {Function} props.onClose - Callback when the user closes the details
 */
const SignatureDetails = ({ signatureId, onClose }) => {
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load signature details on mount
  useEffect(() => {
    const loadSignature = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!signatureId) {
          setError('No signature ID provided');
          setLoading(false);
          return;
        }
        
        const data = await signatureService.getSignature(signatureId);
        setSignature(data);
      } catch (error) {
        console.error('Error loading signature:', error);
        setError('Failed to load signature details');
      } finally {
        setLoading(false);
      }
    };
    
    loadSignature();
  }, [signatureId]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (error || !signature) {
    return (
      <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
        <div className="flex items-center text-red-500 mb-2">
          <AlertCircle size={20} className="mr-2" />
          <span className="font-semibold">Error</span>
        </div>
        <p className="text-red-400">{error || 'Failed to load signature'}</p>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <h2 className="text-xl font-semibold mb-4">Signature Details</h2>
      
      <div className="space-y-6">
        {/* Signature preview */}
        <div>
          <h3 className="text-sm uppercase text-gray-400 mb-2">Signature Preview</h3>
          <div className="bg-white rounded-lg p-4">
            <img 
              src={signature.signatureData} 
              alt="Signature" 
              className="max-h-32 mx-auto"
            />
          </div>
        </div>
        
        {/* Document info */}
        <div>
          <h3 className="text-sm uppercase text-gray-400 mb-2">Document Information</h3>
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-400">Document Name</p>
                <p className="font-medium">{signature.documentName}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-400">Document Status</p>
                <div className="flex items-center">
                  {signature.documentStatus === 'completed' ? (
                    <Check size={16} className="text-green-500 mr-1" />
                  ) : (
                    <AlertCircle size={16} className="text-yellow-500 mr-1" />
                  )}
                  <p className="font-medium capitalize">{signature.documentStatus.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Signer info */}
        <div>
          <h3 className="text-sm uppercase text-gray-400 mb-2">Signer Information</h3>
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <User size={18} className="text-blue-400 mr-2" />
              <div>
                <p className="font-medium">{signature.signer.name}</p>
                <p className="text-sm text-gray-400">{signature.signer.email}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Signature metadata */}
        <div>
          <h3 className="text-sm uppercase text-gray-400 mb-2">Signature Metadata</h3>
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-400">Type</p>
                <div className="flex items-center">
                  <FileText size={16} className="text-blue-400 mr-1" />
                  <p className="font-medium capitalize">{signature.signatureType}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-400">Signed At</p>
                <div className="flex items-center">
                  <Calendar size={16} className="text-blue-400 mr-1" />
                  <p className="font-medium">{new Date(signature.signedAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Position information */}
        {signature.coordinates && (
          <div>
            <h3 className="text-sm uppercase text-gray-400 mb-2">Position Information</h3>
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-400">Page</p>
                  <div className="flex items-center">
                    <MapPin size={16} className="text-blue-400 mr-1" />
                    <p className="font-medium">{signature.coordinates.page}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-gray-400">X Coordinate</p>
                  <p className="font-medium">{signature.coordinates.x}px</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-400">Y Coordinate</p>
                  <p className="font-medium">{signature.coordinates.y}px</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer with close button */}
      <div className="flex justify-end mt-6">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-blue-600 rounded-md text-white hover:bg-blue-500"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default SignatureDetails; 